/**
 * The Overview's "Suggestions" on the Apps tab: at most MAX_SUGGESTIONS hints, most urgent first, each from a fact
 * the page already has (never a guess). `action` is what the hint's button does: 'logs' (read the log), 'start',
 * 'config', 'quality', 'api', or null (the hint is the advice).
 *
 * Facts: `{ state, spec, run, builder, port, secretsMissing, failingChecks, endpoints }`; `state` is serviceState's
 * ('running' | 'stopped' | 'building' | 'failed' | 'crashed'; 'device' for a native device app, utils/deviceApps.js:
 * nothing to start, no start command or port to ask for), `run` howItRuns' kind, `builder` its builder (null: no
 * stack detected), `endpoints` the count (null while unknown), the others counts or null.
 */
export const MAX_SUGGESTIONS = 5;
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

export function serviceSuggestions(facts = {}) {
  const { state, spec = null, run = 'code', builder = null, port = null, secretsMissing = 0, failingChecks = 0, endpoints = null } = facts;
  const out = [];
  if (state === 'failed') { out.push({ id: 'build-failed', text: 'The build failed: read the log to see why.', action: 'logs' }); }
  if (state === 'crashed') { out.push({ id: 'crashed', text: 'It stopped by itself: the log says why.', action: 'logs' }); }
  if (state === 'stopped') { out.push({ id: 'not-running', text: 'It is not running: start it to try it.', action: 'start' }); }
  if (run === 'code' && state !== 'device' && !spec?.start && !builder) {
    out.push({ id: 'no-start', text: 'Flow cannot tell how to start it: add a "start" command to .flow/service.json.', action: null });
  }
  if (state === 'running' && !port) {
    out.push({ id: 'no-port', text: 'It runs but Flow knows no port for it: listen on $PORT or set "port" in .flow/service.json.', action: null });
  }
  if (secretsMissing > 0) { out.push({ id: 'secrets', text: `${plural(secretsMissing, 'secret')} still to set.`, action: 'config' }); }
  if (failingChecks > 0) { out.push({ id: 'quality', text: `${plural(failingChecks, 'quality check')} failing: ${failingChecks === 1 ? 'it blocks' : 'they block'} Publish.`, action: 'quality' }); }
  if (endpoints === 0) { out.push({ id: 'no-endpoints', text: 'No API endpoints found in its code.', action: 'api' }); }
  return out.slice(0, MAX_SUGGESTIONS);
}
